export interface AsyncState {
  loading: boolean;
  error: string | null;
}

export const INITIAL_ASYNC_STATE: AsyncState = {
  loading: false,
  error: null,
};

// Higher-order function to wrap async operations
export function withAsyncHandler<Args extends any[], Return>(
  setState: (updates: Partial<AsyncState>) => void,
  asyncFn: (...args: Args) => Promise<Return>
) {
  return async (...args: Args): Promise<Return | null> => {
    setState({ loading: true, error: null });
    try {
      const result = await asyncFn(...args);
      setState({ loading: false });
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setState({
        error: errorMessage,
        loading: false,
      });
      return null;
    }
  };
}

// Helper to create async state with common actions
export function createAsyncHelpers<T extends AsyncState>(
  set: (updates: Partial<T>) => void
) {
  return {
    setLoading: (loading: boolean) => set({ loading } as Partial<T>),
    setError: (error: string | null) =>
      set({ error, loading: false } as Partial<T>),
    clearError: () => set({ error: null } as Partial<T>),

    // Create async action wrapper
    createAsyncAction: <Args extends any[], Return>(
      asyncFn: (...args: Args) => Promise<Return>
    ) =>
      withAsyncHandler<Args, Return>(
        (updates) => set(updates as Partial<T>),
        asyncFn
      ),
  };
}
